import { PageLoader } from '@ver-uds/react';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Application } from '@vis-auth/vis-identity-client';
import { ConfirmationRequired } from './sub-pages/ConfirmationRequired';
import { EnrollmentState, UserAccountForm } from './utils/constants';
import { NewUserAccountEve } from './sub-pages/NewUserAccountEve';
import { TermsOfUse } from './sub-pages/TermsOfUse';
import FullWidthLayout from '../../layouts/FullWidthLayout';
import { useOneTimeCodeApi } from '../../services/IdentityApi';
import { useEnvironment } from '../../context/Environment/EnvironmentContext';

const EveEnroll = (): JSX.Element => {
  const [enrollmentStatus, setEnrollmentStatus] = useState<EnrollmentState | undefined>(undefined);
  const [emailAddress, setEmailAddress] = useState<string>();
  const [authErrorCode, setAuthErrorCode] = useState<string>('');
  const [enrollError, setEnrollError] = useState<boolean>(false);
  const [showPageLoader, setShowPageLoader] = useState<boolean>(false);
  const formMethods = useForm<UserAccountForm>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // If Enrollment OTC Validation failed, set passed in error code to display alert
  const OTCError = sessionStorage.getItem('OTCError');
  useEffect(() => {
    if (OTCError) {
      setEnrollmentStatus(EnrollmentState.TERMS_ACCEPTED);
      setAuthErrorCode(OTCError);
    }
    sessionStorage.removeItem('OTCError');
  }, [OTCError]);

  const { LOGIN_DOT_GOV_AUTH_CLIENTID } = useEnvironment();
  const OneTimeCodeApi = useOneTimeCodeApi();

  const onSubmit: SubmitHandler<UserAccountForm> = async (data: UserAccountForm): Promise<void> => {
    setEnrollError(false);
    try {
      setShowPageLoader(true);
      await OneTimeCodeApi.sendOneTimeCode({
        sendOtcRequest: {
          clientId: LOGIN_DOT_GOV_AUTH_CLIENTID,
          emailAddress: data.emailAddress,
          app: Application.EVERIFY,
        },
      });
      setShowPageLoader(false);
      setEnrollmentStatus(EnrollmentState.ENROLLMENT_ACCEPTED);
      setEmailAddress(data.emailAddress);
    } catch (e) {
      setShowPageLoader(false);
      setEnrollError(true);
    }
  };

  if (enrollmentStatus === EnrollmentState.TERMS_ACCEPTED) {
    return (
      <FullWidthLayout title="Create New Company Account" headerTitle="E-Verify Enrollment">
        <div className="margin-y-4">
          {showPageLoader && <PageLoader label="Loading" sectionOverlay />}
          <NewUserAccountEve
            formMethods={formMethods}
            onSubmit={onSubmit}
            submissionError={!showPageLoader && enrollError}
            isLoading={showPageLoader}
            authErrorCode={authErrorCode}
          />
        </div>
      </FullWidthLayout>
    );
  }

  if (enrollmentStatus === EnrollmentState.ENROLLMENT_ACCEPTED) {
    return (
      <FullWidthLayout title="Check Email Confirmation" headerTitle="E-Verify Enrollment">
        <div className="margin-y-4">
          <ConfirmationRequired email={emailAddress} />
        </div>
      </FullWidthLayout>
    );
  }

  return (
    <FullWidthLayout title="Use Agreement" headerTitle="E-Verify Enrollment">
      <div className="margin-y-4">
        <TermsOfUse setTermsAccepted={() => setEnrollmentStatus(EnrollmentState.TERMS_ACCEPTED)} />
      </div>
    </FullWidthLayout>
  );
};

export default EveEnroll;
